"use client"

import { Head, router } from "@inertiajs/react"
import { MusicNote01, SearchLg } from "@untitledui/icons"
import { useEffect, useRef, useState } from "react"

import {
  type PlaylistRow,
  PlaylistsTable,
} from "@/components/admin/playlists/playlists-table"
import { EmptyState } from "@/components/ui/empty-state"
import { Input } from "@/components/ui/input"
import { PaginationCardMinimal } from "@/components/ui/pagination"
import { Select } from "@/components/ui/select"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import { adminPlaylistsPath } from "@/routes"
import type { Pagination } from "@/types/contents/pagination"

interface Filters {
  q: string | null
  content: string
}

interface IndexProps {
  playlists: PlaylistRow[]
  pagination: Pagination
  filters: Filters
}

const CONTENT_ITEMS = [
  { id: "all", label: "All playlists" },
  { id: "public", label: "Public" },
  { id: "missing_onwards", label: "Missing onwards" },
]

export default function PlaylistsIndex({
  playlists,
  pagination,
  filters,
}: IndexProps) {
  const [query, setQuery] = useState(filters.q ?? "")

  const visit = (params: Partial<Filters> & { page?: number }) => {
    router.get(
      adminPlaylistsPath(),
      {
        q: params.q ?? filters.q ?? undefined,
        content: params.content ?? filters.content,
        page: params.page ?? 1,
      },
      { preserveState: true, replace: true },
    )
  }

  // Debounced search — fires only once typing pauses, and never on mount.
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    const timeout = setTimeout(() => visit({ q: query || undefined }), 300)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const isFiltered = Boolean(filters.q) || filters.content !== "all"

  return (
    <DashboardLayout>
      <Head title="Playlists" />
      <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 pt-8 pb-12 lg:px-8">
        <header className="flex flex-col gap-0.5">
          <h1 className="text-primary font-display md:text-display-xs text-xl font-semibold">
            Playlists
          </h1>
          <p className="text-md text-tertiary">
            Every playlist, with its visibility and onward placements. Filter to
            find public ones with no outro.
          </p>
        </header>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Input
            size="sm"
            icon={SearchLg}
            aria-label="Search playlists"
            placeholder="Search by title or owner"
            value={query}
            onChange={setQuery}
            className="sm:max-w-xs"
          />
          <Select
            size="sm"
            aria-label="Filter by content"
            items={CONTENT_ITEMS}
            selectedKey={filters.content}
            onChange={(key) => key != null && visit({ content: String(key) })}
            className="w-52"
          >
            {(item) => (
              <Select.Item key={item.id} id={item.id}>
                {item.label}
              </Select.Item>
            )}
          </Select>
        </div>

        {playlists.length === 0 ? (
          <EmptyState size="sm" className="py-10">
            <EmptyState.Header pattern="none">
              <EmptyState.FeaturedIcon
                color="gray"
                theme="modern"
                icon={MusicNote01}
              />
            </EmptyState.Header>
            <EmptyState.Content>
              <p className="text-primary text-md text-center font-semibold">
                {isFiltered ? "No playlists match" : "No playlists yet"}
              </p>
              <EmptyState.Description>
                {isFiltered
                  ? "Try a different search or clear the filters."
                  : "Playlists appear here as people build them."}
              </EmptyState.Description>
            </EmptyState.Content>
          </EmptyState>
        ) : (
          <PlaylistsTable
            rows={playlists}
            aria-label="Playlists"
            footer={
              pagination.total > 1 ? (
                <PaginationCardMinimal
                  page={pagination.page}
                  total={pagination.total}
                  onPageChange={(page) => visit({ page })}
                />
              ) : undefined
            }
          />
        )}
      </main>
    </DashboardLayout>
  )
}
